import CharacterCount from '@tiptap/extension-character-count';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classnames from 'classnames';
import noop from 'lodash/noop';
import { EditorView } from 'prosemirror-view';
import React, { useState } from 'react';
import RequiredMark from '../required-mark';
import FontSize from './extensions/font-size';
import SelectionDecoration from './extensions/selection-decoration';
import { isHttpURL, noNewLine } from './rte-utils';
import { TextEditorToolbar } from './text-editor-toolbar/text-editor-toolbar';

import './styles.scss';

type Props = {
  ['data-testid']?: string;
  allowEmpty?: boolean;
  bubbleMenu?: boolean;
  className?: string;
  isEditingInitial?: boolean;
  isReadOnly: boolean;
  isRequired?: boolean;
  limit?: number;
  onBlur?: (arg1: undefined) => void;
  onFocus: (arg1: undefined) => void;
  onSave: (arg1: string) => void;
  placeholder: string;
  value: string;
};

const PriorityStrike = Strike.extend({
  // default is 100 lower priority means <s> tag renders inner than other marks
  // https://tiptap.dev/docs/editor/extensions/custom-extensions/extend-existing#priority
  priority: 90,
});

const RichTextEditor = (props: Props) => {
  const {
    value,
    limit,
    className,
    bubbleMenu,
    placeholder,
    isReadOnly,
    isEditingInitial,
    isRequired,
    'data-testid': dataTestid = 'rich-text-editor',
  } = props;
  const [content, setContent] = useState(value);

  const extensions = [
    StarterKit.configure({
      strike: false,
    }),
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Placeholder.configure({
      placeholder,
    }),
    Highlight.configure({ multicolor: true }),
    Link.configure({
      validate: href => isHttpURL(href),
    }),
    Color,
    FontSize,
    TextStyle,
    PriorityStrike,
    SelectionDecoration,
  ];

  if (limit) {
    extensions.push(
      CharacterCount.configure({
        limit,
      }),
    );
    // Ignore enter key presses when the maximum character limit has been reached.
    extensions.push(
      noNewLine((view: EditorView, event: KeyboardEvent) => {
        const characterCount = view.state.doc.textContent.length;
        return characterCount === limit && event.key === 'Enter';
      }),
    );
  }

  const editor = useEditor({
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
    extensions,
    content,
    editable: !isReadOnly,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    editorProps: {
      attributes: {
        'data-testid': dataTestid,
        class: !isReadOnly ? 'EditableContent' : '',
      },
    },
  });

  const [isEditing, setIsEditing] = useState(!!isEditingInitial);
  const handleFocus = () => {
    const { onFocus = noop } = props;
    setIsEditing(true);
    onFocus();
  };

  const handleBlur = () => {
    const { onBlur = noop, onSave, allowEmpty } = props;
    // Checks if editor content consists of whitespace to remove any unnecessary <p> tags created by TipTap
    const html =
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18047
      editor.state.doc.textContent.trim() === '' ? '' : editor.getHTML();
    if (!allowEmpty && html === '') {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18047
      editor.commands.setContent(content);
    } else {
      if (html !== content) onSave(html);
      setContent(html);
    }
    setIsEditing(false);
    onBlur();
  };

  return (
    <div className={classnames('editor-container', className)}>
      {bubbleMenu && editor && <TextEditorToolbar editor={editor} />}
      <EditorContent
        editor={editor}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      {isRequired && (
        <RequiredMark
          className={`required-mark ${isEditing ? 'hide-required-mark' : ''}`}
        />
      )}
    </div>
  );
};

export { RichTextEditor };
export default RichTextEditor;
